// src/core/services/layers.js
import L from "leaflet";
import { PMTiles, leafletRasterLayer } from "pmtiles";
import * as EL from "esri-leaflet";
import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import proj4 from "proj4";

if (typeof globalThis !== "undefined" && !globalThis.proj4) {
  globalThis.proj4 = proj4;
}

export function createLayerService({ events, ui, map }) {
  const LAYERS_KEY = "hydrotools:layers:v1";
  const stack = []; // ordered bottom -> top

  function _snapshot() {
    return stack.map((rec) => ({
      id: rec.id,
      toolId: rec.toolId,
      name: rec.name,
      group: rec.group ?? null,
      kind: rec.kind,
      type: rec.type,
      url: rec.url,
      options: rec.options ?? {},
      opacity: rec.opacity,
      visible: rec.visible,
      meta: rec.meta ?? {},
      volatile: !!rec.volatile,
    }));
  }

  function _persist() {
    try {
      const persistable = _snapshot().filter((x) => !x.volatile);
      localStorage.setItem(LAYERS_KEY, JSON.stringify(persistable));
    } catch (err) {
      console.error("[Layers] persist failed", err);
    }
  }

  function _bringStackToFront() {
    // Leaflet's shared canvas renderer always paints/hit-tests newly created
    // paths on top of the draw order, regardless of prior bringToFront() calls.
    // Tools that manage their own Leaflet layers outside this stack (e.g.
    // slip-delivery's raw featureGroups) can silently bury tracked overlays
    // (like the tile-index) whenever they redraw and recreate canvas paths.
    // bringOverlaysToFront() lets those tools ask us to reassert order.
    for (const rec of stack) {
      if (!rec.visible) continue;
      if (rec.type === "managed") {
        rec.managed?.bringToFront?.();
        continue;
      }
      if (rec.leaflet?.bringToFront) rec.leaflet.bringToFront();
      if (rec._raster?.bringToFront) rec._raster.bringToFront();
      if (rec.leaflet?.eachLayer) {
        rec.leaflet.eachLayer((child) => {
          if (child?.bringToFront) child.bringToFront();
        });
      }
    }
  }

  function _applyStacking({ reassertOrder = true } = {}) {
    // bringToFront() walks every shape of every geojson-vector layer (see
    // _bringStackToFront) -- for a layer with thousands of points (e.g. a photo trail),
    // that's thousands of canvas linked-list splices + redraw requests on every single
    // call. Removing a layer never needs the rest of the stack's order reasserted --
    // Leaflet's canvas draw-order linked list preserves everyone else's relative order
    // on its own when one entry is spliced out -- so removeLayer() passes
    // reassertOrder:false to skip this pass. Every operation that adds or re-shows a
    // layer still needs it, since Leaflet paints a (re-)added layer on top by default.
    if (reassertOrder) _bringStackToFront();
    _persist();
    events.emit("layers:changed", listLayers());
  }

  function bringOverlaysToFront() {
    _bringStackToFront();
  }

  function addLayer(toolId, def) {
    const id = def.id ?? `layer-${crypto.randomUUID?.() ?? Math.random().toString(16).slice(2)}`;

    let rec;
    let leaflet;
    if (def.type === "xyz") {
      leaflet = L.tileLayer(def.url, {
        crossOrigin: true,
        ...(def.options ?? {}),
      });
    } else if (def.type === "wms") {
      const wmsOptions = { crossOrigin: true, ...(def.options ?? {}) };
      const crsCode = String(wmsOptions.crsCode || "").toUpperCase();
      if (crsCode === "EPSG:4326") wmsOptions.crs = L.CRS.EPSG4326;
      else if (crsCode === "EPSG:3857") wmsOptions.crs = L.CRS.EPSG3857;
      delete wmsOptions.crsCode;
      leaflet = L.tileLayer.wms(def.url, wmsOptions);
    } else if (def.type === "esri-image") {
      leaflet = EL.imageMapLayer({ url: def.url, ...(def.options ?? {}) });
    } else if (def.type === "esri-feature") {
      const featureOptions = { ...(def.options ?? {}) };
      if (featureOptions.interactive == null) featureOptions.interactive = true;
      const styleBuilder = typeof featureOptions.styleBuilder === "function"
        ? featureOptions.styleBuilder
        : null;
      const popupBuilder = typeof featureOptions.popupBuilder === "function"
        ? featureOptions.popupBuilder
        : null;
      const tooltipBuilder = typeof featureOptions.tooltipBuilder === "function"
        ? featureOptions.tooltipBuilder
        : null;
      const tooltipOptions = featureOptions.tooltipOptions && typeof featureOptions.tooltipOptions === "object"
        ? featureOptions.tooltipOptions
        : { permanent: true, direction: "center", className: "zone-label-tip" };
      delete featureOptions.styleBuilder;
      delete featureOptions.popupBuilder;
      delete featureOptions.tooltipBuilder;
      delete featureOptions.tooltipOptions;
      leaflet = EL.featureLayer({
        url: def.url,
        ...featureOptions,
        style: (feature) => buildEsriFeatureStyle(feature, def.opacity, styleBuilder),
        onEachFeature: (popupBuilder || tooltipBuilder)
          ? (feature, layer) => {
              if (popupBuilder) {
                const popupHtml = popupBuilder(feature);
                if (popupHtml) layer.bindPopup(popupHtml, { maxWidth: 360 });
              }
              if (tooltipBuilder) {
                const tip = tooltipBuilder(feature);
                if (tip) layer.bindTooltip(tip, tooltipOptions);
              }
            }
          : undefined,
      });
      if (popupBuilder && leaflet?.bindPopup) {
        try {
          leaflet.bindPopup((layer) => {
            const popupHtml = popupBuilder(layer?.feature || layer);
            return popupHtml || "";
          }, { maxWidth: 360 });
        } catch {}
      }
    } else if (def.type === "geojson-vector") {
      const vectorOptions = { ...(def.options ?? {}) };
      const styleBuilder = typeof vectorOptions.styleBuilder === "function" ? vectorOptions.styleBuilder : null;
      const popupBuilder = typeof vectorOptions.popupBuilder === "function" ? vectorOptions.popupBuilder : null;
      const tooltipBuilder = typeof vectorOptions.tooltipBuilder === "function" ? vectorOptions.tooltipBuilder : null;
      const onPopupOpen = typeof vectorOptions.onPopupOpen === "function" ? vectorOptions.onPopupOpen : null;
      const tooltipOptions = vectorOptions.tooltipOptions && typeof vectorOptions.tooltipOptions === "object"
        ? vectorOptions.tooltipOptions
        : { permanent: true, direction: "center", className: "zone-label-tip" };
      // Labels only bound past this zoom — with hundreds/thousands of features, permanent
      // tooltips rendered at every zoom level overlap into an unreadable, sluggish mess.
      const tooltipMinZoom = Number.isFinite(Number(vectorOptions.tooltipMinZoom)) ? Number(vectorOptions.tooltipMinZoom) : null;
      // Strictly opt-in on two separate flags (pointToLayer function, or pointsAsCircles+
      // styleBuilder) — with neither supplied, behavior is byte-for-byte what it was before
      // pointToLayer support existed at all (undefined, same as never passing the key), so
      // every existing geojson-vector caller is provably unaffected. Point features with no
      // pointToLayer fall back to Leaflet's default L.marker (a DOM <img>+shadow per feature)
      // — fine for a handful of points, both visually wrong (pins, not styled dots) and slow
      // (DOM nodes instead of one shared canvas draw) for hundreds/thousands (e.g. a photo
      // trail) — callers with that many points should pass pointsAsCircles: true.
      const pointToLayer = typeof vectorOptions.pointToLayer === "function"
        ? vectorOptions.pointToLayer
        : (vectorOptions.pointsAsCircles && styleBuilder)
          ? (feature, latlng) => L.circleMarker(latlng, styleBuilder(feature) || {})
          : undefined;
      leaflet = L.geoJSON(def.data, {
        pointToLayer,
        // No custom renderer here — this must share the map's default canvas (preferCanvas:
        // true in core/map.js) like every other vector layer type. A private L.canvas()
        // renderer used to sit here, which creates its own separate <canvas> DOM element;
        // unlike SVG, a canvas intercepts pointer events across its whole bounding box, and
        // Leaflet can't reorder two separate canvases relative to each other the way it
        // reorders shapes within one shared canvas — so whichever canvas-backed layer landed
        // later in the DOM silently ate every click on this layer underneath it.
        style: (feature) => buildEsriFeatureStyle(feature, def.opacity, styleBuilder),
        onEachFeature: (popupBuilder || tooltipBuilder)
          ? (feature, layer) => {
              if (popupBuilder) {
                const popupHtml = popupBuilder(feature);
                if (popupHtml) {
                  layer.bindPopup(popupHtml, { maxWidth: 360 });
                  if (onPopupOpen) {
                    layer.on("popupopen", (e) => {
                      const el = e.popup.getElement();
                      if (el) onPopupOpen(feature, el);
                    });
                  }
                }
              }
              if (tooltipBuilder) {
                const tip = tooltipBuilder(feature);
                if (tip) {
                  layer._tileTooltipContent = tip;
                  if (tooltipMinZoom == null) layer.bindTooltip(tip, tooltipOptions);
                }
              }
            }
          : undefined,
      });
      if (tooltipBuilder && tooltipMinZoom != null) {
        // Bind tooltips only for features currently in view — at the required zoom the
        // dataset can still span far more of the map than the visible viewport, and binding
        // every feature's tooltip regardless of visibility means hundreds of off-screen DOM
        // nodes get repositioned on every pan, which is what actually caused the sluggishness.
        const applyTooltipVisibility = () => {
          const zoom = map.instance.getZoom();
          const bounds = zoom >= tooltipMinZoom ? map.instance.getBounds() : null;
          leaflet.eachLayer((child) => {
            if (!child._tileTooltipContent) return;
            const inView = !!bounds && child.getBounds && bounds.intersects(child.getBounds());
            if (inView && !child.getTooltip?.()) child.bindTooltip(child._tileTooltipContent, tooltipOptions);
            else if (!inView && child.getTooltip?.()) child.unbindTooltip();
          });
        };
        map.instance.on("zoomend moveend", applyTooltipVisibility);
        applyTooltipVisibility();
        leaflet._tileZoomHandler = applyTooltipVisibility;
      }
    } else if (def.type === "pmtiles-raster") {
      const source = new PMTiles(def.url);
      leaflet = leafletRasterLayer(source, def.options ?? {});
      source.getHeader()
        .then((h) => {
          const bounds = L.latLngBounds(
            [h.minLat, h.minLon],
            [h.maxLat, h.maxLon],
          );
          if (!bounds.isValid()) return;
          if (!bounds.contains(map.instance.getCenter())) {
            ui?.toast?.("PMTiles loaded. Pan/zoom to the dataset extent.");
          }
        })
        .catch((err) => {
          console.error("[Layers] PMTiles header load failed", err);
          ui?.toast?.(`PMTiles failed to load: ${def.url}`);
        });
    } else if (def.type === "geotiff-url") {
      leaflet = L.layerGroup();
      const url = def.url;
      console.warn("[Layers Debug] Loading GeoTIFF", def.name || def.url);
      ui?.toast?.(`Loading GeoTIFF: ${def.name || def.url}`);
      const customProjectionCode = String(def?.options?.projectionCode || "").trim();
      const customProjectionDef = String(def?.options?.projectionDef || "").trim();
      const worldFileAffine = parseWorldFileAffine(def?.options?.worldFileAffine);
      const geographicBounds = parseGeographicBounds(def?.options?.geographicBounds);
      const resolvedProjectionCode = customProjectionDef
        ? (customProjectionCode || `LOCAL:GEOTIFF:${id}`)
        : customProjectionCode;
      const georasterProjectionValue = normalizeGeorasterProjectionValue(resolvedProjectionCode);
      if (resolvedProjectionCode && customProjectionDef) {
        try {
          if (!proj4.defs(resolvedProjectionCode)) proj4.defs(resolvedProjectionCode, customProjectionDef);
          if (globalThis?.proj4?.defs && !globalThis.proj4.defs(resolvedProjectionCode)) {
            globalThis.proj4.defs(resolvedProjectionCode, customProjectionDef);
          }
        } catch (err) {
          console.warn("[Layers] GeoTIFF projection registration failed", err);
        }
      }
      // For local files without explicit geographicBounds, auto-fetch WGS84 bounds from server
      // in parallel with the TIFF download. This avoids "unsupported projection" errors from
      // georaster-layer-for-leaflet when the TIFF uses a projected CRS not in proj4.defs.
      const localFilePathMatch = /[?&]path=([^&]+)/.exec(url || "");
      const localFilePath = localFilePathMatch ? decodeURIComponent(localFilePathMatch[1]) : null;
      const serverBoundsPromise = (!geographicBounds && localFilePath)
        ? fetch(`/api/system/geotiff-wgs84?path=${encodeURIComponent(localFilePath)}`)
            .then((r) => r.ok ? r.json() : null)
            .then((info) => {
              if (!Array.isArray(info?.wgs84Bounds) || info.wgs84Bounds.length !== 4) return null;
              const [w, s, e, n] = info.wgs84Bounds;
              return parseGeographicBounds([w, e, s, n]);
            })
            .catch(() => null)
        : Promise.resolve(null);

      Promise.all([
        fetch(url)
          .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
          .then((ab) => parseGeoraster(ab)),
        serverBoundsPromise,
      ])
        .then(([georaster, serverBounds]) => {
          if (!rec) return;
          console.warn("[Layers Debug] Parsed GeoTIFF", rec.name);
          ui?.toast?.(`Parsed GeoTIFF: ${rec.name}`);
          const effectiveBounds = geographicBounds || serverBounds;
          const useGeographicBounds = !!effectiveBounds;
          if (useGeographicBounds) {
            georaster.projection = 4326;
            georaster.xmin = effectiveBounds.west;
            georaster.xmax = effectiveBounds.east;
            georaster.ymin = effectiveBounds.south;
            georaster.ymax = effectiveBounds.north;
            const w = Number(georaster.width);
            const h = Number(georaster.height);
            if (Number.isFinite(w) && w > 0) georaster.pixelWidth = (effectiveBounds.east - effectiveBounds.west) / w;
            if (Number.isFinite(h) && h > 0) georaster.pixelHeight = (effectiveBounds.north - effectiveBounds.south) / h;
            console.info("[Layers] Using geographic bounds", effectiveBounds);
          } else if (worldFileAffine) {
            applyWorldFileToGeoraster(georaster, worldFileAffine);
          }
          if (!useGeographicBounds && georasterProjectionValue) {
            georaster.projection = georasterProjectionValue;
          }
          // IMPORTANT:
          // Do not use the projected fast-preview imageOverlay path by default.
          // That path stretches the projected image into a geographic rectangle
          // and can introduce visible drift/rotation misalignment vs vectors.
          // Use GeoRasterLayer reprojection for projected rasters unless caller
          // explicitly opts into fast mode.
          const allowFastProjectedPreview = def?.options?.projectedPreviewMode === "fast";
          if (!useGeographicBounds && resolvedProjectionCode && customProjectionDef && allowFastProjectedPreview) {
            const imageBounds = computeProjectedGeoTiffBounds(georaster, resolvedProjectionCode);
            if (!imageBounds?.isValid?.()) {
              throw new Error(`Projected GeoTIFF bounds are invalid for ${rec.name || def.name || def.url}`);
            }
            const rendered = georaster.toCanvas
              ? georaster.toCanvas({
                  width: Number(georaster.width) || undefined,
                  height: Number(georaster.height) || undefined,
                })
              : null;
            return Promise.resolve(rendered).then((canvas) => {
              if (!canvas?.toDataURL) {
                throw new Error("Projected GeoTIFF canvas rendering is unavailable.");
              }
              const overlay = L.imageOverlay(canvas.toDataURL("image/png"), imageBounds, {
                opacity: rec.opacity ?? 1,
                interactive: false,
                crossOrigin: true,
                className: "leaflet-raster-pixelated",
              });
              rec._raster = overlay;
              overlay.addTo(leaflet);
              if (rec.visible && !map.instance.hasLayer(leaflet)) leaflet.addTo(map.instance);
              if (!imageBounds.contains(map.instance.getCenter())) {
                ui?.toast?.(`GeoTIFF loaded: ${rec.name}`);
              }
            });
          }
          // For WGS84-bounded rasters (PDAL exports, server-bounds path), render directly
          // to canvas and display as imageOverlay. GeoRasterLayer's tile pipeline has
          // proven unreliable for small projected-to-geographic rasters; this path is
          // simpler, deterministic, and produces correct output.
          if (useGeographicBounds) {
            const overlayBounds = L.latLngBounds(
              [effectiveBounds.south, effectiveBounds.west],
              [effectiveBounds.north, effectiveBounds.east],
            );
            if (!overlayBounds.isValid()) throw new Error("GeoTIFF geographic bounds are invalid.");
            const canvas = renderGeoRasterToCanvas(georaster, def?.options);
            const overlay = L.imageOverlay(canvas.toDataURL("image/png"), overlayBounds, {
              opacity: rec.opacity ?? 1,
              interactive: false,
              className: "leaflet-raster-pixelated",
            });
            rec._raster = overlay;
            overlay.addTo(leaflet);
            if (rec.visible && !map.instance.hasLayer(leaflet)) leaflet.addTo(map.instance);
            if (def?.options?.autoZoom) {
              try { map.instance.fitBounds(overlayBounds.pad?.(0.06) || overlayBounds); } catch {}
            }
            ui?.toast?.(`GeoTIFF loaded: ${rec.name}`);
            return;
          }

          const pxWidth = Number(georaster?.width);
          const pxHeight = Number(georaster?.height);
          const totalPixels = (Number.isFinite(pxWidth) && Number.isFinite(pxHeight)) ? (pxWidth * pxHeight) : 0;
          // Large-raster preview mode is only safe for already-geographic rasters.
          // Projected rasters need true reprojection via GeoRasterLayer.
          if (!resolvedProjectionCode && totalPixels > 30_000_000 && georaster?.toCanvas) {
            const previewMaxDim = 2048;
            const scale = previewMaxDim / Math.max(pxWidth || 1, pxHeight || 1);
            const previewWidth = Math.max(1, Math.round((pxWidth || 1) * Math.min(1, scale)));
            const previewHeight = Math.max(1, Math.round((pxHeight || 1) * Math.min(1, scale)));
            const previewBounds = L.latLngBounds(
              [Number(georaster?.ymin), Number(georaster?.xmin)],
              [Number(georaster?.ymax), Number(georaster?.xmax)],
            );
            if (previewBounds?.isValid?.()) {
              return Promise.resolve(
                georaster.toCanvas({ width: previewWidth, height: previewHeight })
              ).then((canvas) => {
                if (!canvas?.toDataURL) throw new Error("GeoTIFF preview canvas failed.");
                const overlay = L.imageOverlay(canvas.toDataURL("image/png"), previewBounds, {
                  opacity: rec.opacity ?? 1,
                  interactive: false,
                  crossOrigin: true,
                  className: "leaflet-raster-pixelated",
                });
                rec._raster = overlay;
                overlay.addTo(leaflet);
                if (rec.visible && !map.instance.hasLayer(leaflet)) leaflet.addTo(map.instance);
                if (def?.options?.autoZoom) {
                  try { map.instance.fitBounds(previewBounds.pad?.(0.06) || previewBounds); } catch {}
                }
              });
            }
          }
          const raster = new GeoRasterLayer({
            georaster,
            proj4,
            opacity: rec.opacity ?? 1,
            resolution: Number(def?.options?.resolution || 256),
            pixelValuesToColorFn: buildGeoTiffColorFn(georaster, def?.options),
          });
          rec._raster = raster;
          raster.addTo(leaflet);
          if (rec.visible && !map.instance.hasLayer(leaflet)) leaflet.addTo(map.instance);
          try {
            const b = raster.getBounds?.();
            if (def?.options?.autoZoom && b?.isValid?.()) {
              try { map.instance.fitBounds(b.pad?.(0.06) || b); } catch {}
            }
            if (b?.isValid?.() && !b.contains(map.instance.getCenter())) {
              ui?.toast?.(`GeoTIFF loaded: ${rec.name}`);
            }
          } catch {}
        })
        .catch((err) => {
          console.error("[Layers] GeoTIFF load failed", err);
          ui?.toast?.(`GeoTIFF failed: ${def.name || def.url} (${String(err?.message || err)})`);
        });
    } else if (def.type === "tile-url") {
      const tileOpts = def.options || {};
      const b = tileOpts.bounds;
      const tileBounds = (b && Number.isFinite(b.south) && Number.isFinite(b.north))
        ? L.latLngBounds([b.south, b.west], [b.north, b.east])
        : null;
      leaflet = L.tileLayer(def.url, {
        maxNativeZoom: Number(tileOpts.maxNativeZoom || 18),
        minZoom: Number(tileOpts.minNativeZoom || 1),
        maxZoom: Number(tileOpts.maxZoom || 26),
        opacity: Number(def.opacity ?? 1),
        tileSize: 256,
        crossOrigin: true,
        ...(tileBounds ? { bounds: tileBounds } : {}),
      });
      if (tileOpts.autoZoom && tileBounds?.isValid?.()) {
        try { map.instance.fitBounds(tileBounds.pad?.(0.06) || tileBounds); } catch {}
      }
    } else if (def.type === "leaflet-group") {
      leaflet = def.leaflet;
      if (!leaflet) throw new Error("leaflet-group requires def.leaflet");
    } else if (def.type === "managed") {
      leaflet = null;
    } else {
      throw new Error(`Unsupported layer type: ${def.type}`);
    }

    rec = {
      id,
      toolId,
      name: def.name ?? id,
      group: def.group ?? null,
      kind: def.kind ?? "overlay", // "basemap" or "overlay"
      type: def.type,
      url: def.url,
      options: def.options ?? {},
      opacity: def.opacity ?? 1,
      visible: def.visible ?? true,
      meta: def.meta ?? {},
      volatile: !!def.volatile,
      leaflet,
      _raster: null,
      managed: def.managed ?? null,
    };

    stack.push(rec);

    if (rec.type === "managed") {
      if (rec.visible) rec.managed?.setVisible?.(true);
      rec.managed?.setOpacity?.(rec.opacity);
      rec.managed?.setMeta?.(rec.meta ?? {});
    } else {
      if (rec.visible) leaflet.addTo(map.instance);
      leaflet?.setOpacity?.(rec.opacity);
    }

    _applyStacking();
    return id;
  }

  function removeLayer(id) {
    const idx = stack.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const rec = stack[idx];
    if (rec.type === "managed") {
      try { rec.managed?.remove?.(); } catch {}
    } else if (rec.leaflet) {
      if (rec.leaflet._tileZoomHandler) map.instance.off("zoomend moveend", rec.leaflet._tileZoomHandler);
      map.instance.removeLayer(rec.leaflet);
    }
    if (rec.volatile && typeof rec.url === "string" && rec.url.startsWith("blob:")) {
      try { URL.revokeObjectURL(rec.url); } catch {}
    }
    stack.splice(idx, 1);
    _applyStacking({ reassertOrder: false });
  }

  function removeLayersByTool(toolId) {
    const toRemove = stack.filter((r) => r.toolId === toolId).map((r) => r.id);
    for (const id of toRemove) removeLayer(id);
  }

  function setOpacity(id, opacity, options = {}) {
    const emit = options?.emit !== false;
    const persist = options?.persist !== false;
    const rec = stack.find((r) => r.id === id);
    if (!rec) return;
    rec.opacity = Math.max(0, Math.min(1, opacity));
    if (rec.type === "managed") {
      rec.managed?.setOpacity?.(rec.opacity);
    } else {
      if (rec.type === "esri-feature" || rec.type === "geojson-vector") applyEsriFeatureOpacity(rec);
      else rec.leaflet?.setOpacity?.(rec.opacity);
      rec._raster?.setOpacity?.(rec.opacity);
    }
    if (persist) _persist();
    if (emit) events.emit("layers:changed", listLayers());
  }

  function setName(id, name) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return false;
    const next = String(name ?? "").trim();
    if (!next) return false;
    rec.name = next;
    _persist();
    events.emit("layers:changed", listLayers());
    return true;
  }

  function setGroup(id, group) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return false;
    rec.group = group ? String(group) : null;
    _persist();
    events.emit("layers:changed", listLayers());
    return true;
  }

  function updateLayer(id, patch = {}) {
    const rec = stack.find((r) => r.id === id);
    if (!rec || !patch || typeof patch !== "object") return false;
    let needsStackApply = false;
    if (Object.prototype.hasOwnProperty.call(patch, "name")) {
      const n = String(patch.name ?? "").trim();
      if (n) rec.name = n;
    }
    if (Object.prototype.hasOwnProperty.call(patch, "group")) {
      rec.group = patch.group ? String(patch.group) : null;
    }
    if (Object.prototype.hasOwnProperty.call(patch, "meta")) {
      rec.meta = { ...(rec.meta ?? {}), ...(patch.meta ?? {}) };
      if (rec.type === "managed") rec.managed?.setMeta?.(rec.meta);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "opacity")) {
      rec.opacity = Math.max(0, Math.min(1, Number(patch.opacity) || 0));
      if (rec.type === "managed") rec.managed?.setOpacity?.(rec.opacity);
      else {
        if (rec.type === "esri-feature" || rec.type === "geojson-vector") applyEsriFeatureOpacity(rec);
        else rec.leaflet?.setOpacity?.(rec.opacity);
        rec._raster?.setOpacity?.(rec.opacity);
      }
    }
    if (Object.prototype.hasOwnProperty.call(patch, "visible")) {
      rec.visible = !!patch.visible;
      if (rec.type === "managed") rec.managed?.setVisible?.(rec.visible);
      else if (rec.visible) rec.leaflet?.addTo?.(map.instance);
      else if (rec.leaflet) map.instance.removeLayer(rec.leaflet);
      needsStackApply = true;
    }
    if (needsStackApply) _applyStacking();
    else {
      _persist();
      events.emit("layers:changed", listLayers());
    }
    return true;
  }

  function setVisible(id, visible) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return;
    rec.visible = !!visible;
    if (rec.type === "managed") {
      rec.managed?.setVisible?.(rec.visible);
    } else {
      if (rec.visible) rec.leaflet.addTo(map.instance);
      else map.instance.removeLayer(rec.leaflet);
    }
    _applyStacking();
  }

  function move(id, direction) {
    const idx = stack.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const newIdx = direction === "up" ? idx + 1 : idx - 1; // up = toward top
    if (newIdx < 0 || newIdx >= stack.length) return;
    const [rec] = stack.splice(idx, 1);
    stack.splice(newIdx, 0, rec);
    _applyStacking();
  }

  function reorderKind(kind, idsTopToBottom) {
    if (!Array.isArray(idsTopToBottom) || !idsTopToBottom.length) return;
    const wanted = idsTopToBottom
      .map((id) => stack.find((r) => r.id === id && r.kind === kind))
      .filter(Boolean);
    if (!wanted.length) return;
    const replacementBottomToTop = [...wanted].reverse();
    let cursor = 0;
    const next = stack.map((rec) => {
      if (rec.kind !== kind) return rec;
      const repl = replacementBottomToTop[cursor];
      cursor += 1;
      return repl ?? rec;
    });
    stack.splice(0, stack.length, ...next);
    _applyStacking();
  }

  function reorderSubset(idsTopToBottom) {
    if (!Array.isArray(idsTopToBottom) || !idsTopToBottom.length) return;
    const wanted = idsTopToBottom
      .map((id) => stack.find((r) => r.id === id))
      .filter(Boolean);
    if (!wanted.length) return;
    const wantedSet = new Set(wanted.map((r) => r.id));
    const replacementBottomToTop = [...wanted].reverse();
    let cursor = 0;
    const next = stack.map((rec) => {
      if (!wantedSet.has(rec.id)) return rec;
      const repl = replacementBottomToTop[cursor];
      cursor += 1;
      return repl ?? rec;
    });
    stack.splice(0, stack.length, ...next);
    _applyStacking();
  }

  function zoomTo(id) {
    const rec = stack.find((r) => r.id === id);
    if (!rec) return false;
    const isGeoBounds = (bounds) => {
      if (!bounds?.isValid?.()) return false;
      const sw = bounds.getSouthWest?.();
      const ne = bounds.getNorthEast?.();
      return sw && ne
        && sw.lat >= -90 && sw.lat <= 90 && ne.lat >= -90 && ne.lat <= 90
        && sw.lng >= -180 && sw.lng <= 180 && ne.lng >= -180 && ne.lng <= 180;
    };
    let b = rec.type === "managed"
      ? rec.managed?.getBounds?.()
      : (rec.leaflet?.getBounds?.() || rec._raster?.getBounds?.());
    if (!isGeoBounds(b)) {
      const raw = rec?.meta?.bounds;
      if (Array.isArray(raw) && raw.length === 4 && raw.every((v) => Number.isFinite(Number(v)))) {
        const west = Number(raw[0]);
        const south = Number(raw[1]);
        const east = Number(raw[2]);
        const north = Number(raw[3]);
        b = L.latLngBounds([south, west], [north, east]);
      }
    }
    if (!isGeoBounds(b)) return false;
    map.instance.fitBounds(b.pad?.(0.12) || b);
    return true;
  }

  function listLayers() {
    // Return bottom->top; UI can render top-first if preferred
    return _snapshot();
  }

  function _restorePersisted() {
    let raw = null;
    try {
      raw = localStorage.getItem(LAYERS_KEY);
    } catch (err) {
      console.error("[Layers] read persisted failed", err);
      return;
    }
    if (!raw) return;

    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      console.error("[Layers] parse persisted failed", err);
      return;
    }
    if (!Array.isArray(parsed)) return;

    for (const item of parsed) {
      if (!item || typeof item !== "object") continue;
      if (!item.type || !item.url) continue;
      try {
        addLayer(item.toolId ?? "overlay-manager", {
          id: item.id,
          name: item.name,
          group: item.group ?? null,
          kind: item.kind ?? "overlay",
          type: item.type,
          url: item.url,
          options: item.options ?? {},
          opacity: Number.isFinite(Number(item.opacity)) ? Number(item.opacity) : 1,
          visible: item.visible !== false,
          meta: item.meta ?? {},
        });
      } catch (err) {
        console.error("[Layers] restore layer failed", item, err);
      }
    }
  }

  function setLayerWhere(id, where) {
    const rec = stack.find((r) => r.id === id);
    if (!rec || rec.type !== "esri-feature") return false;
    try {
      rec.leaflet?.setWhere?.(String(where || "1=1"));
      return true;
    } catch (err) {
      console.error("[Layers] setLayerWhere failed", err);
      return false;
    }
  }

  function setLayerStyle(id, styleFn) {
    const rec = stack.find((r) => r.id === id);
    if (!rec || (rec.type !== "esri-feature" && rec.type !== "geojson-vector")) return false;
    try {
      rec.leaflet?.setStyle?.(styleFn);
      return true;
    } catch (err) {
      console.error("[Layers] setLayerStyle failed", err);
      return false;
    }
  }

  _restorePersisted();

  return {
    addLayer,
    removeLayer,
    removeLayersByTool,
    setOpacity,
    setVisible,
    setLayerWhere,
    setLayerStyle,
    setName,
    setGroup,
    updateLayer,
    move,
    reorderKind,
    reorderSubset,
    zoomTo,
    listLayers,
    bringOverlaysToFront,
  };
}

function computeProjectedGeoTiffBounds(georaster, projectionCode) {
  const xmin = Number(georaster?.xmin);
  const xmax = Number(georaster?.xmax);
  const ymin = Number(georaster?.ymin);
  const ymax = Number(georaster?.ymax);
  if (![xmin, xmax, ymin, ymax].every(Number.isFinite)) return null;
  const corners = [
    [xmin, ymin],
    [xmin, ymax],
    [xmax, ymin],
    [xmax, ymax],
  ]
    .map(([x, y]) => {
      const result = proj4(projectionCode, "EPSG:4326", [x, y]);
      const lon = Number(result?.[0]);
      const lat = Number(result?.[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return [lat, lon];
    })
    .filter(Boolean);
  if (!corners.length) return null;
  return L.latLngBounds(corners);
}

function parseWorldFileAffine(value) {
  if (!Array.isArray(value) || value.length < 6) return null;
  const nums = value.slice(0, 6).map((n) => Number(n));
  if (!nums.every((n) => Number.isFinite(n))) return null;
  return nums;
}

function normalizeGeorasterProjectionValue(projectionCode) {
  const value = String(projectionCode || "").trim();
  if (!value) return "";
  const epsgMatch = /^EPSG:(\d+)$/i.exec(value);
  if (epsgMatch) return Number(epsgMatch[1]);
  return value;
}

function parseGeographicBounds(value) {
  if (!Array.isArray(value) || value.length < 4) return null;
  const nums = value.slice(0, 4).map((n) => Number(n));
  const [west, east, south, north] = nums;
  if (![west, east, south, north].every(Number.isFinite)) return null;
  if (west >= east || south >= north) return null;
  if (west < -180 || east > 180 || south < -90 || north > 90) return null;
  return { west, east, south, north };
}


function applyWorldFileToGeoraster(georaster, affine) {
  if (!georaster || !affine) return false;
  const width = Number(georaster?.width);
  const height = Number(georaster?.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return false;
  const [A, D, B, E, C, F] = affine.map((n) => Number(n));
  if (![A, B, C, D, E, F].every((n) => Number.isFinite(n))) return false;

  const mapCorner = (col, row) => ([
    (A * col) + (B * row) + C,
    (D * col) + (E * row) + F,
  ]);
  const corners = [
    mapCorner(-0.5, -0.5),
    mapCorner(width - 0.5, -0.5),
    mapCorner(-0.5, height - 0.5),
    mapCorner(width - 0.5, height - 0.5),
  ];
  const xs = corners.map((pt) => Number(pt?.[0])).filter((n) => Number.isFinite(n));
  const ys = corners.map((pt) => Number(pt?.[1])).filter((n) => Number.isFinite(n));
  if (xs.length !== 4 || ys.length !== 4) return false;

  georaster.xmin = Math.min(...xs);
  georaster.xmax = Math.max(...xs);
  georaster.ymin = Math.min(...ys);
  georaster.ymax = Math.max(...ys);
  if (Math.abs(B) < 1e-12 && Math.abs(D) < 1e-12) {
    georaster.pixelWidth = Math.abs(A);
    georaster.pixelHeight = Math.abs(E);
  }
  return true;
}

function buildGeoTiffColorFn(georaster, options = {}) {
  const nodataSet = new Set();
  const pushNoData = (v) => {
    const n = Number(v);
    if (Number.isFinite(n)) nodataSet.add(n);
  };

  pushNoData(georaster?.noDataValue);
  (Array.isArray(options?.nodataValues) ? options.nodataValues : []).forEach(pushNoData);
  if (options?.nodataValue !== undefined) pushNoData(options.nodataValue);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const asByte = (v) => clamp(Math.round(Number(v) || 0), 0, 255);
  const mins = Array.isArray(georaster?.mins) ? georaster.mins : [];
  const maxs = Array.isArray(georaster?.maxs) ? georaster.maxs : [];
  const rgbAppearsNormalized01 = [0, 1, 2].every((idx) => {
    const mn = Number(mins[idx]);
    const mx = Number(maxs[idx]);
    if (!Number.isFinite(mn) || !Number.isFinite(mx)) return false;
    return mn >= -0.01 && mx <= 1.5;
  });
  const alphaMax = Number(maxs[3]);
  const alphaDenom = Number.isFinite(alphaMax) && alphaMax > 1.5
    ? (alphaMax > 255 ? alphaMax : 255)
    : 1;
  const rampName = String(options?.colorRamp || "viridis").toLowerCase();
  const useAlpha = options?.useAlpha !== false;
  const stretchMinOpt = Number(options?.stretchMin);
  const stretchMaxOpt = Number(options?.stretchMax);
  const ramp = getSimpleRamp(rampName);
  const stretchMin = Number.isFinite(stretchMinOpt) ? stretchMinOpt : (Number.isFinite(mins[0]) ? mins[0] : 0);
  const stretchMax = Number.isFinite(stretchMaxOpt) ? stretchMaxOpt : (Number.isFinite(maxs[0]) ? maxs[0] : 255);

  return (values) => {
    if (!Array.isArray(values) || !values.length) return null;
    const nums = values.map((v) => Number(v));
    if (nums.every((v) => !Number.isFinite(v))) return null;

    if (nums.length >= 3) {
      const rgb = nums.slice(0, 3);
      // For multiband rasters, only treat as nodata when all color bands are nodata.
      // This avoids dropping valid pixels where one channel can legitimately be 0.
      if (rgb.every((v) => nodataSet.has(v))) return null;
      const r = asByte(rgbAppearsNormalized01 ? (nums[0] * 255) : nums[0]);
      const g = asByte(rgbAppearsNormalized01 ? (nums[1] * 255) : nums[1]);
      const b = asByte(rgbAppearsNormalized01 ? (nums[2] * 255) : nums[2]);
      const a = (useAlpha && nums.length >= 4 && Number.isFinite(nums[3])) ? clamp(Number(nums[3]) / alphaDenom, 0, 1) : 1;
      if (a <= 0) return null;
      return `rgba(${r},${g},${b},${a})`;
    }

    const v = nums[0];
    if (nodataSet.has(v)) return null;
    const span = Math.max(1e-9, stretchMax - stretchMin);
    const t = clamp((v - stretchMin) / span, 0, 1);
    const [r, g, b] = sampleRamp(ramp, t);
    return `rgba(${r},${g},${b},1)`;
  };
}

function getSimpleRamp(name) {
  const ramps = {
    grayscale: [[0, 0, 0], [255, 255, 255]],
    viridis: [[68, 1, 84], [59, 82, 139], [33, 145, 140], [94, 201, 98], [253, 231, 37]],
    plasma: [[13, 8, 135], [106, 0, 168], [177, 42, 144], [225, 100, 98], [252, 166, 54], [240, 249, 33]],
    inferno: [[0, 0, 4], [66, 10, 104], [147, 38, 103], [221, 81, 58], [252, 165, 10], [252, 255, 164]],
    spectral: [[94, 79, 162], [50, 136, 189], [102, 194, 165], [230, 245, 152], [253, 174, 97], [213, 62, 79]],
    terrain: [[51, 102, 0], [153, 204, 102], [230, 220, 170], [170, 130, 90], [255, 255, 255]],
    copper: [[30, 20, 10], [130, 80, 40], [230, 150, 90], [255, 220, 180]],
    blue_orange: [[20, 60, 160], [90, 160, 230], [240, 170, 70], [170, 80, 20]],
  };
  return ramps[name] || ramps.viridis;
}

function sampleRamp(ramp, t) {
  const pts = Array.isArray(ramp) && ramp.length >= 2 ? ramp : [[0, 0, 0], [255, 255, 255]];
  const clamped = Math.max(0, Math.min(1, Number(t) || 0));
  const scaled = clamped * (pts.length - 1);
  const i0 = Math.floor(scaled);
  const i1 = Math.min(pts.length - 1, i0 + 1);
  const f = scaled - i0;
  const c0 = pts[i0];
  const c1 = pts[i1];
  const lerp = (a, b) => Math.round((Number(a) || 0) + ((Number(b) || 0) - (Number(a) || 0)) * f);
  return [lerp(c0[0], c1[0]), lerp(c0[1], c1[1]), lerp(c0[2], c1[2])];
}

function renderGeoRasterToCanvas(georaster, options = {}) {
  const w = Number(georaster?.width) || 0;
  const h = Number(georaster?.height) || 0;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  if (!w || !h) return canvas;

  const ctx = canvas.getContext("2d");
  const imageData = ctx.createImageData(w, h);
  const d = imageData.data;

  const nodata = Number(georaster?.noDataValue);
  const nodataIsFinite = Number.isFinite(nodata);
  const rampName = String(options?.colorRamp || "viridis").toLowerCase();
  const ramp = getSimpleRamp(rampName);

  const band0 = georaster?.values?.[0];
  if (!band0) { ctx.putImageData(imageData, 0, 0); return canvas; }

  // band0 can be a flat TypedArray (band0[i] is a number) or a 2D array of rows
  const isFlat = typeof band0[0] === "number";

  // Compute actual valid data range (first pass) so we can auto-stretch correctly.
  // The user-provided stretchMin/Max defaults (e.g. 0–100) often don't match the
  // actual data range (e.g. 2.1–4.2m depth), producing near-black invisible pixels.
  let validMin = Infinity;
  let validMax = -Infinity;
  for (let row = 0; row < h; row++) {
    const rowData = isFlat ? band0 : band0[row];
    if (!rowData) continue;
    const rowOffset = isFlat ? row * w : 0;
    for (let col = 0; col < w; col++) {
      const v = Number(rowData[rowOffset + col]);
      if (Number.isFinite(v) && !(nodataIsFinite && v === nodata)) {
        if (v < validMin) validMin = v;
        if (v > validMax) validMax = v;
      }
    }
  }

  const hasValidData = Number.isFinite(validMin) && Number.isFinite(validMax) && validMax > validMin;
  const stretchMinOpt = Number(options?.stretchMin);
  const stretchMaxOpt = Number(options?.stretchMax);
  const explicitMin = Number.isFinite(stretchMinOpt) ? stretchMinOpt : null;
  const explicitMax = Number.isFinite(stretchMaxOpt) ? stretchMaxOpt : null;

  // Auto-stretch when the explicit range squishes all valid data into < 15% of the
  // ramp (e.g. default 0–100 with depth data 2.1–4.2 → only 2% of ramp used → invisible).
  let stretchMin = explicitMin ?? (hasValidData ? validMin : 0);
  let stretchMax = explicitMax ?? (hasValidData ? validMax : 255);
  if (hasValidData && explicitMin !== null && explicitMax !== null) {
    const explicitRange = Math.max(1e-9, explicitMax - explicitMin);
    const dataFraction = (validMax - validMin) / explicitRange;
    if (dataFraction < 0.15) {
      stretchMin = validMin;
      stretchMax = validMax;
    }
  }
  const span = Math.max(1e-9, stretchMax - stretchMin);

  // Second pass: render pixels
  for (let row = 0; row < h; row++) {
    const rowData = isFlat ? band0 : band0[row];
    if (!rowData) continue;
    const rowOffset = isFlat ? row * w : 0;
    for (let col = 0; col < w; col++) {
      const v = Number(rowData[rowOffset + col]);
      const px = (row * w + col) * 4;
      if (!Number.isFinite(v) || (nodataIsFinite && v === nodata)) {
        d[px + 3] = 0;
      } else {
        const t = Math.max(0, Math.min(1, (v - stretchMin) / span));
        const [r, g, b] = sampleRamp(ramp, t);
        d[px] = r;
        d[px + 1] = g;
        d[px + 2] = b;
        d[px + 3] = 255;
      }
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function buildEsriFeatureStyle(feature, opacity, styleBuilder) {
  const base = typeof styleBuilder === "function" ? styleBuilder(feature) : {};
  const weight = Number.isFinite(Number(base?.weight)) ? Number(base.weight) : 1;
  const fillOpacity = Number.isFinite(Number(base?.fillOpacity)) ? Number(base.fillOpacity) : 0.35;
  const lineOpacity = Number.isFinite(Number(base?.opacity)) ? Number(base.opacity) : 0.9;
  const alpha = Math.max(0, Math.min(1, Number(opacity) || 0));
  return {
    color: base?.color || "#4b5563",
    weight,
    opacity: lineOpacity * alpha,
    fillColor: base?.fillColor || base?.color || "#93c5fd",
    fillOpacity: fillOpacity * alpha,
    dashArray: base?.dashArray || null,
  };
}

function applyEsriFeatureOpacity(rec) {
  if (!rec?.leaflet?.setStyle) return;
  const styleBuilder = typeof rec?.options?.styleBuilder === "function"
    ? rec.options.styleBuilder
    : null;
  rec.leaflet.setStyle((feature) => buildEsriFeatureStyle(feature, rec.opacity, styleBuilder));
}
